import React from "react";
import Select, {
  components,
  SingleValueProps,
  CSSObjectWithLabel,
  ControlProps,
} from "react-select";
import cx from "classnames";
import { DropdownType } from "@/enums";
import { useTranslation } from "react-i18next";

export type DropdownProperties = {
  type: DropdownType | string,
  placeholder: string,
  isDisabled: boolean,
  isLoading?: boolean,
  value: any,
  options?: Array<{label: string, value: string | string[] | number | {periodId: number, startDate: string, endDate: string}, icon?: React.ReactNode}>,
  'aria-label': string,
  onChange: (value: any) => void,
  styles?: React.CSSProperties,
  isMulti?: boolean,
  entityLabel?: string,
  className?: string
}

function SingleValue({
  children,
  ...props
}: SingleValueProps<any>) {
  if (!props.data.icon) return <components.SingleValue {...props}>{children}</components.SingleValue>

  return (
    <components.SingleValue {...props}>
      <div className="d-flex align-items-center">
        <span style={{ marginRight: "0.5rem" }}>{props.data.icon}</span>
        <span>{props.data.label}</span>
      </div>
    </components.SingleValue>
  );
}

export const Dropdown: React.FC<DropdownProperties> = ({
  isDisabled = false,
  placeholder = "",
  type = DropdownType.PRIMARY,
  value = null,
  options = [],
  isLoading = false,
  'aria-label': ariaLabel,
  onChange,
  styles = {},
  entityLabel,
  isMulti = false,
  className
}) => {
  const { t } = useTranslation(['reports']);
  let primaryDropdown = type === DropdownType.PRIMARY;
  let headerDropdown = type === DropdownType.HEADER;

  const dropdownStyle = cx({
    teacherDisabledSelect: isDisabled,
    teacherSecondarySelect: !primaryDropdown && !headerDropdown,
    teacherPrimarySelect: primaryDropdown,
    teacherHeaderSelect: headerDropdown,
  }, className);

  const dropdownPrefixStyle = cx({
    teacherDisabledSelect: isDisabled,
    teacherSecondarySelect: !isDisabled && !primaryDropdown && !headerDropdown,
    teacherPrimarySelect: !isDisabled && primaryDropdown,
    teacherHeaderSelect: !isDisabled && headerDropdown,
  });

  // ensure label of selected value matches option labels
  const clonedValue = value ? { ...value } : value;
  const selectedOption = options.find(({value}) => value === clonedValue?.value);
  if (selectedOption) value.label = selectedOption.label;

  const formatOptionLabel = (
    {value, label, icon}: {value: any, label: string, icon?: React.ReactNode},
    {context}: {context: 'menu' | 'value'}
  ) => {
    if (icon) {
      return (
        <div className="d-flex align-items-center">
          <span style={{ marginRight: "0.5rem" }}>{icon}</span>
          <span>{label}</span>
        </div>
      );
    }
    if (value === "all") {
      if (context === "menu") {
        return t("whole_class"); // in dropdown list
      } else {
        return label; // selected value shows class name in the header
      }
    }
    return label;
  };

  const customStyles = {
    control: (
      base: CSSObjectWithLabel
    ): CSSObjectWithLabel => ({
      ...base,
      ...styles,
    }),
  };

  return (
    <>
      { entityLabel && <span className="entityLabel">{entityLabel}</span> }
      <Select
        menuPlacement="auto"
        isSearchable={false}
        className={dropdownStyle}
        classNamePrefix={dropdownPrefixStyle}
        value={value}
        onChange={onChange}
        options={options}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        isDisabled={isLoading}
        aria-label={ariaLabel || ''}
        styles={customStyles}
        components={{ SingleValue }}
        isMulti={isMulti}
      />
    </>
  );
};

export default Dropdown;
